import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  Avatar,
  Button,
  Typography
} from "@mui/material";
import { BCPTrophy, BCPMedal } from "../../../components/icons/bcp/BCPIcons";
import StatBox from "./StatBox";

export default function FactionRankingTable({
  rows,
  theme,
  label,
  childLabel,
  childRecordType,
  onAnalyze
}) {
  const [expandedRows, setExpandedRows] = useState({});

  const isDark = theme.palette.mode === "dark";

  const toggleRow = (position) => {
    setExpandedRows((prev) => ({
      ...prev,
      [position]: !prev[position]
    }));
  };

  if (!rows || rows.length === 0) return null;

  const avgWinRate =
    rows.reduce((sum, r) => sum + r.stats.winRate, 0) / rows.length;

  const closestToAvg = rows.reduce((closest, r) =>
    Math.abs(r.stats.winRate - avgWinRate) <
    Math.abs(closest.stats.winRate - avgWinRate)
      ? r
      : closest
  ).position;

  const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(" ");
    if (words.length === 1) {
      return name.substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const hasUnrankedRows = rows.some(
    (r) => r.rank === null || r.rank === undefined
  );

  const getRankDisplay = (rank, position) => {
    const isUnranked = rank === null || rank === undefined;

    const asterisk = isUnranked ? (
      <Box
        component="span"
        sx={{
          color: theme.palette.text.secondary,
          fontSize: "0.8rem",
          fontWeight: 700,
          ml: 0.3,
          opacity: 0.65,
          lineHeight: 1,
          verticalAlign: "super"
        }}
      >
        *
      </Box>
    ) : null;

    if (rank === 1) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5
          }}
        >
          <BCPTrophy size="sm" style={{ color: "#FFD700" }} />
          <Box sx={{ color: "#FFD700", fontWeight: 700, fontSize: "1rem" }}>
            {position}
          </Box>
        </Box>
      );
    }

    if (rank === 2) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5
          }}
        >
          <BCPMedal size="sm" style={{ color: "#C0C0C0" }} />
          <Box sx={{ color: "#C0C0C0", fontWeight: 700, fontSize: "1rem" }}>
            {position}
          </Box>
        </Box>
      );
    }

    if (rank === 3) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5
          }}
        >
          <BCPMedal size="sm" style={{ color: "#CD7F32" }} />
          <Box sx={{ color: "#CD7F32", fontWeight: 700, fontSize: "1rem" }}>
            {position}
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          component="span"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 500,
            fontSize: "0.9rem"
          }}
        >
          {position}
          {asterisk}
        </Box>
      </Box>
    );
  };

  const getColoredStat = (value, type) => {
    const colors = {
      wins: isDark ? "#4ade80" : "#16a34a",
      losses: isDark ? "#f87171" : "#dc2626",
      draws: isDark ? "#fbbf24" : "#d97706"
    };

    return (
      <Box component="span" sx={{ color: colors[type], fontWeight: 600 }}>
        {value}
      </Box>
    );
  };

  const getWinRateColor = (winRate, position) => {
    if (position === closestToAvg) return isDark ? "#9ca3af" : "#6b7280";
    if (winRate > avgWinRate) return isDark ? "#4ade80" : "#16a34a";
    return isDark ? "#f87171" : "#dc2626";
  };

  const getStripLabel = (winRate, position) => {
    if (position === closestToAvg) return "Near meta average";
    if (winRate > avgWinRate) return "Above meta average";
    return "Below meta average";
  };

  const renderWinRateCell = (winRate, position) => {
    const color = getWinRateColor(winRate, position);
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end"
        }}
      >
        <Typography
          sx={{
            color,
            fontWeight: 700,
            fontSize: "0.875rem",
            lineHeight: 1.3,
            fontVariantNumeric: "tabular-nums"
          }}
        >
          {winRate}%
        </Typography>
        <Box
          sx={{
            width: 80,
            height: 5,
            borderRadius: 3,
            backgroundColor: isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)",
            mt: 0.5,
            overflow: "hidden"
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${winRate}%`,
              borderRadius: 3,
              background: `linear-gradient(90deg, ${color}55, ${color})`
            }}
          />
        </Box>
      </Box>
    );
  };

  const headerCellSx = {
    fontWeight: 700,
    fontSize: "0.7rem",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: theme.palette.text.secondary,
    borderBottom: `2px solid ${theme.palette.divider}`
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
        boxShadow: isDark
          ? "0 2px 8px rgba(0,0,0,0.3)"
          : "0 2px 8px rgba(0,0,0,0.1)",
        borderRadius: 1
      }}
    >
      <Table>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: isDark
                ? "rgba(255,255,255,0.03)"
                : "rgba(0,0,0,0.02)"
            }}
          >
            <TableCell sx={{ ...headerCellSx, width: 80 }}>#</TableCell>
            <TableCell sx={headerCellSx}>{label || "Faction"}</TableCell>
            <TableCell align="right" sx={{ ...headerCellSx, width: 100 }}>
              Games
            </TableCell>
            <TableCell align="right" sx={{ ...headerCellSx, width: 100 }}>
              Wins
            </TableCell>
            <TableCell align="right" sx={{ ...headerCellSx, width: 100 }}>
              Losses
            </TableCell>
            <TableCell align="right" sx={{ ...headerCellSx, width: 100 }}>
              Draws
            </TableCell>
            <TableCell align="right" sx={{ ...headerCellSx, width: 120 }}>
              Win %
            </TableCell>
            <TableCell align="center" sx={{ ...headerCellSx, width: 200 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <React.Fragment key={row.position}>
              <TableRow
                sx={{
                  "&:hover": {
                    backgroundColor: isDark
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.04)"
                  }
                }}
              >
                <TableCell
                  sx={{
                    py: 1.5,
                    pl: 0,
                    position: "relative",
                    width: 80
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 3,
                      backgroundColor: getWinRateColor(
                        row.stats.winRate,
                        row.position
                      ),
                      borderRadius: "0 4px 4px 0",
                      overflow: "hidden",
                      transition: "width 0.25s ease",
                      display: "flex",
                      alignItems: "center",
                      zIndex: 2,
                      cursor: "default",
                      "&:hover": {
                        width: 160
                      },
                      "& .strip-label": {
                        opacity: 0,
                        transition: "opacity 0.1s ease"
                      },
                      "&:hover .strip-label": {
                        opacity: 1,
                        transition: "opacity 0.15s ease 0.1s"
                      }
                    }}
                  >
                    <Box
                      className="strip-label"
                      sx={{
                        pl: 1.5,
                        pr: 1,
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        color: "rgba(255,255,255,0.95)",
                        whiteSpace: "nowrap",
                        lineHeight: 1.3
                      }}
                    >
                      {getStripLabel(row.stats.winRate, row.position)}
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    {getRankDisplay(row.rank, row.position)}
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar
                      src={row.faction.icon || undefined}
                      alt={row.faction.name}
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: "0.75rem",
                        fontWeight: 600
                      }}
                    >
                      {!row.faction.icon && getInitials(row.faction.name)}
                    </Avatar>
                    <Box
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 700
                      }}
                    >
                      {row.faction.name}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5, width: 100 }}>
                  <StatBox
                    theme={theme}
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {row.stats.games}
                  </StatBox>
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5, width: 100 }}>
                  <StatBox theme={theme}>
                    {getColoredStat(row.stats.wins, "wins")}
                  </StatBox>
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5, width: 100 }}>
                  <StatBox theme={theme}>
                    {getColoredStat(row.stats.losses, "losses")}
                  </StatBox>
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5, width: 100 }}>
                  <StatBox theme={theme}>
                    {getColoredStat(row.stats.draws, "draws")}
                  </StatBox>
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5, width: 120 }}>
                  {renderWinRateCell(row.stats.winRate, row.position)}
                </TableCell>
                <TableCell align="center" sx={{ py: 1.5 }}>
                  <Box
                    sx={{ display: "flex", gap: 1, justifyContent: "center" }}
                  >
                    {row.children && row.children.length > 0 && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => toggleRow(row.position)}
                        sx={{
                          minWidth: 70,
                          height: 28,
                          px: 1.5,
                          py: 0.5,
                          fontSize: "0.75rem",
                          backgroundColor: isDark ? "#8b5cf6" : "#7c3aed",
                          "&:hover": {
                            backgroundColor: isDark ? "#7c3aed" : "#6d28d9"
                          },
                          fontWeight: 600,
                          textTransform: "none",
                          boxShadow: 1
                        }}
                      >
                        {childLabel}
                      </Button>
                    )}
                    {onAnalyze && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onAnalyze && onAnalyze(row)}
                        sx={{
                          minWidth: 70,
                          height: 28,
                          px: 1.5,
                          py: 0.5,
                          fontSize: "0.75rem",
                          backgroundColor: isDark ? "#3b82f6" : "#2563eb",
                          "&:hover": {
                            backgroundColor: isDark ? "#2563eb" : "#1d4ed8"
                          },
                          fontWeight: 600,
                          textTransform: "none",
                          boxShadow: 1
                        }}
                      >
                        Analyze
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>

              {row.children && row.children.length > 0 && (
                <TableRow>
                  <TableCell sx={{ py: 0, px: 0, border: 0 }} colSpan={8}>
                    <Collapse
                      in={expandedRows[row.position]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box
                        sx={{
                          backgroundColor: isDark
                            ? "rgba(0,0,0,0.3)"
                            : "rgba(0,0,0,0.04)"
                        }}
                      >
                        <Table>
                          <TableBody>
                            {row.children.map((child) => (
                              <TableRow
                                key={child.id}
                                sx={{
                                  "&:hover": {
                                    backgroundColor: isDark
                                      ? "rgba(0,0,0,0.4)"
                                      : "rgba(0,0,0,0.06)"
                                  }
                                }}
                              >
                                <TableCell sx={{ width: 80, py: 1.5 }} />
                                <TableCell sx={{ py: 1.5 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1.5,
                                      pl: 4
                                    }}
                                  >
                                    <Avatar
                                      src={
                                        child[childRecordType]?.icon ||
                                        undefined
                                      }
                                      alt={child[childRecordType]?.name}
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        fontSize: "0.7rem",
                                        fontWeight: 600
                                      }}
                                    >
                                      {!child[childRecordType]?.icon &&
                                        getInitials(
                                          child[childRecordType]?.name
                                        )}
                                    </Avatar>
                                    <Box
                                      sx={{
                                        color: theme.palette.text.primary,
                                        fontSize: "0.875rem"
                                      }}
                                    >
                                      {child[childRecordType]?.name}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ py: 1.5, width: 100 }}
                                >
                                  <StatBox
                                    theme={theme}
                                    sx={{ color: theme.palette.text.secondary }}
                                  >
                                    {child.stats.games}
                                  </StatBox>
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ py: 1.5, width: 100 }}
                                >
                                  <StatBox theme={theme}>
                                    {getColoredStat(child.stats.wins, "wins")}
                                  </StatBox>
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ py: 1.5, width: 100 }}
                                >
                                  <StatBox theme={theme}>
                                    {getColoredStat(
                                      child.stats.losses,
                                      "losses"
                                    )}
                                  </StatBox>
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ py: 1.5, width: 100 }}
                                >
                                  <StatBox theme={theme}>
                                    {getColoredStat(child.stats.draws, "draws")}
                                  </StatBox>
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ py: 1.5, width: 120 }}
                                >
                                  {renderWinRateCell(child.stats.winRate)}
                                </TableCell>
                                <TableCell sx={{ width: 200, py: 1.5 }} />
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
      {hasUnrankedRows && (
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
            backgroundColor: isDark
              ? "rgba(255,255,255,0.02)"
              : "rgba(0,0,0,0.01)"
          }}
        >
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: theme.palette.text.secondary,
              fontStyle: "italic",
              opacity: 0.7
            }}
          >
            * Ranking withheld due to limited game data — statistical accuracy
            may be reduced.
          </Typography>
        </Box>
      )}
    </TableContainer>
  );
}
